import React from "react";
import {formatSimmsDateTimeKorean} from "../../../../../components/commonmethod/CommonMethod";
import {DetailKvTable, DetailPanel} from "../../../../../components/detailLayout";
import {DetailKvListValue} from "../../../../../components/detailLayout/DetailKvListValue";
import {KV_LIST_TYPES} from "../../../../../components/detailLayout/detailKvListLabels";

export const formatUserNames = (users) => {
    if (!Array.isArray(users) || users.length === 0) return "";
    return users.map((user) => user?.name).filter(Boolean).join(", ");
};

export const formatAssetNames = (assets) => {
    if (!Array.isArray(assets) || assets.length === 0) return "";
    return assets
        .map((asset) => asset?.assetName ?? asset?.name)
        .filter(Boolean)
        .join(", ");
};

export const formatContractNames = (contracts) => {
    if (!Array.isArray(contracts) || contracts.length === 0) return "";
    return contracts.map((contract) => contract?.name).filter(Boolean).join(", ");
};

export const formatCompanies = (...companies) =>
    companies.map((company) => company?.name).filter(Boolean).join(" / ");

export const formatAuditUser = (user) => {
    if (user == null || user === "") return "";
    if (typeof user === "string") return user;
    if (typeof user === "object") {
        return user.name ?? user.userName ?? user.login ?? user.label ?? "";
    }
    return "";
};

export const customStringType = (t) => {
    const s = (t == null ? "" : String(t)).trim().toUpperCase();
    return s === "INPUT" || s === "DATE" || s === "TEXT" ? s : "INPUT";
};

export {AssetDetailKvColgroupFour} from "../../../../../components/detailLayout/detailKvColgroup";

export function displayText(value, type) {
    if (value == null || value === "") {
        return <span className="asset-detail-kv-value asset-detail-kv-empty">&nbsp;</span>;
    }
    if (typeof value === "object") {
        const text = formatAuditUser(value);
        return (
            <span className="asset-detail-kv-value">{text || "\u00A0"}</span>
        );
    }
    if (type === "date") {
        return (
            <span className="asset-detail-kv-value">
                {formatSimmsDateTimeKorean(value) || "\u00A0"}
            </span>
        );
    }
    const text = String(value);
    const multiline = text.includes("\n");
    return (
        <span
            className={`asset-detail-kv-value${multiline ? " asset-detail-kv-value--multiline" : ""}`}
        >
            {text}
        </span>
    );
}

export function chunkFieldsIntoRows(fields) {
    const rows = [];
    for (let i = 0; i < fields.length; ) {
        const current = fields[i];
        if (current.wide) {
            rows.push([current]);
            i += 1;
            continue;
        }
        const next = fields[i + 1];
        if (next && !next.wide) {
            rows.push([current, next]);
            i += 2;
        } else {
            rows.push([current]);
            i += 1;
        }
    }
    return rows;
}

export function fieldsToKvCells(fieldDefs, isEditing, renderEdit) {
    return fieldDefs.map((field) => ({
        label: field.label,
        value: field.value,
        type: field.type,
        listType: field.listType,
        required: field.required,
        rowAuto: field.rowAuto,
        editRender:
            field.editKey && isEditing
                ? () => renderEdit(field.editKey, ...(field.editArgs || []))
                : undefined,
    }));
}

export function buildCommonInfoFieldDefs(asset, platFrom, customCodes, customStrings) {
    const fields = [
        {label: "등록일시", value: asset.createdAt, type: "date", editKey: "createdAt"},
        {label: "수정일시", value: asset.updatedAt, type: "date", editKey: "updatedAt"},
        {label: "등록자", value: formatAuditUser(asset.createdBy), editKey: "createdBy"},
        {label: "수정자", value: formatAuditUser(asset.updatedBy), editKey: "updatedBy"},
        {label: "자산명", value: asset.assetName, required: true, editKey: "assetName"},
        {label: "자산코드", value: asset.assetCode, editKey: "assetCode"},
        {label: "부가정보", value: asset.etc, editKey: "etc", wide: true},
        {label: "시스템", value: asset.project?.name, editKey: "project"},
        {label: "업무그룹", value: asset.task?.name, editKey: "task"},
        {label: "자산그룹", value: asset.group?.name, required: true, editKey: "group"},
        {label: "자산위치", value: asset.location?.name, editKey: "location"},
    ];

    if (platFrom === "ITM") {
        fields.push(
            {label: "SLA 그룹", value: asset.slaGroup?.name, editKey: "slaGroup"},
            {label: "부서", value: asset.department?.name, editKey: "department"},
        );
    } else {
        fields.push({label: "부서", value: asset.department?.name, editKey: "department", wide: true});
    }

    (customCodes || []).forEach((code, index) => {
        const each = asset.assetCustomCodes?.find((e) => e?.customCode?.id === code.id);
        fields.push({
            label: code.name,
            value: each?.value?.name,
            editKey: "customCode",
            editArgs: [code, index],
        });
    });

    (customStrings || []).forEach((code, index) => {
        const each = asset.assetCustomStrings?.find((e) => e?.code?.id === code.id);
        fields.push({
            label: code.name,
            value: each?.value,
            editKey: "customString",
            editArgs: [code, index],
        });
    });

    return fields;
}

export function buildMaintenanceFieldDefs(asset, fieldList) {
    const fields = [
        {
            label: "운영담당자",
            value: asset.operator,
            listType: KV_LIST_TYPES.user,
            editKey: "operator",
            rowAuto: true,
            wide: true,
        },
        {
            label: "자산사용자",
            value: asset.handler,
            listType: KV_LIST_TYPES.user,
            editKey: "handler",
            rowAuto: true,
            wide: true,
        },
        {
            label: "유지보수업체",
            value: formatCompanies(asset.mngPrimary, asset.mngSecondary, asset.mngTertiary),
            editKey: "maintenanceCompanies",
            rowAuto: true,
            wide: true,
        },
        {label: "도입금액", value: asset.importPrice, editKey: "importPrice", wide: true},
        {label: "도입일자", value: asset.importDate, editKey: "importDate", wide: true},
        {label: "End of Service", value: asset.endOfService, editKey: "endOfService", wide: true},
        {label: "유지보수액", value: asset.mngPrice, editKey: "mngPrice", wide: true},
        {label: "유지보수율", value: asset.mngRate, editKey: "mngRate", wide: true},
    ];

    (fieldList || []).forEach((field, index) => {
        const each = asset.assetFields?.find((e) => e?.field?.id === field.id);
        fields.push({
            label: field.title,
            value: each?.value,
            editKey: "assetField",
            editArgs: [field, index],
            wide: true,
        });
    });

    return fields;
}

function renderCellContent(isEditing, editRender, value, type, listType) {
    if (isEditing && typeof editRender === "function") {
        return editRender();
    }
    if (listType) {
        return <DetailKvListValue items={value} listType={listType} />;
    }
    return displayText(value, type);
}

function KvCells({label, value, type, listType, required, isEditing, editRender}) {
    return (
        <>
            <th>
                {label}
                {required && <span className="c-red ml-1">*</span>}
            </th>
            <td>{renderCellContent(isEditing, editRender, value, type, listType)}</td>
        </>
    );
}

export function KvRow({cells, singleColumn = false, tableColumns = 4, isEditing = false, rowAuto = false}) {
    const valueColSpan = Math.max(1, tableColumns - 1);
    const rowClassName = rowAuto ? "asset-detail-kv-row--auto" : undefined;

    if (singleColumn || (cells.length === 1 && cells[0].wide)) {
        const cell = cells[0];
        return (
            <tr className={rowClassName}>
                <th>
                    {cell.label}
                    {cell.required && <span className="c-red ml-1">*</span>}
                </th>
                <td colSpan={valueColSpan}>
                    {renderCellContent(isEditing, cell.editRender, cell.value, cell.type, cell.listType)}
                </td>
            </tr>
        );
    }

    const [left, right] = cells;
    return (
        <tr className={rowClassName}>
            <KvCells {...left} isEditing={isEditing} />
            {right ? (
                <KvCells {...right} isEditing={isEditing} />
            ) : (
                <>
                    <th>&nbsp;</th>
                    <td>&nbsp;</td>
                </>
            )}
        </tr>
    );
}

export {DetailPanel};

export function CommonInfoPanel({rows, isEditing}) {
    return (
        <DetailPanel title="공통정보">
            <DetailKvTable>
                <tbody>
                    {rows.map((row, index) => (
                        <KvRow
                            key={row.key || `common-${index}`}
                            isEditing={isEditing}
                            singleColumn={row.cells.length === 1}
                            cells={row.cells}
                        />
                    ))}
                </tbody>
            </DetailKvTable>
        </DetailPanel>
    );
}

export function MaintenancePanel({rows, isEditing, footer}) {
    return (
        <DetailPanel title="유지관리 & 그룹정보">
            <table className="asset-detail-kv-table">
                <colgroup>
                    <col style={{width: "15%"}} />
                    <col style={{width: "85%"}} />
                </colgroup>
                <tbody>
                    {rows.map((row, index) => (
                        <KvRow
                            key={row.key || `maint-${index}`}
                            isEditing={isEditing}
                            rowAuto={isEditing && row.rowAuto}
                            singleColumn
                            tableColumns={2}
                            cells={row.cells}
                        />
                    ))}
                </tbody>
            </table>
            {footer}
        </DetailPanel>
    );
}
